import { useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import type { CategoryTileViewModel } from '@rocco/ui/tile-category/view-models/category';
import { Image } from '@rocco/components/image';

interface CategoryTilesGridProps {
    categories: CategoryTileViewModel[];
    variant?: 'vertical' | 'horizontal';
    categoryName?: string;
    totalCount?: number;
}

/**
 * Grid layout for category tiles. Supports two variants:
 * - 'vertical': Vertical cards with image on top, text below (used on root /products page)
 * - 'horizontal': Horizontal tiles with image left, text right (used on first-level+ category pages, desktop only)
 *
 * @param categories - The categories to display.
 * @param variant - Layout variant ('vertical' | 'horizontal'). Defaults to 'vertical'.
 * @param categoryName - Category name for the "View All [X] Products" button (horizontal variant only).
 * @param totalCount - Total count for the results display (horizontal variant only).
 * @returns A grid of category tiles.
 */
export const CategoryTilesGrid = ({
    categories,
    variant = 'vertical',
    categoryName,
    totalCount,
}: CategoryTilesGridProps) => {
    const location = useLocation();

    // Preserve existing query params when adding browse=true
    const viewAllUrl = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.set('browse', 'true');
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search]);

    if (!categories.length) {
        return null;
    }

    if (variant === 'horizontal') {
        return (
            <div>
                {/* Desktop horizontal grid */}
                <ul className="hidden gap-x-5 gap-y-12.5 px-12 md:grid md:grid-cols-4">
                    {categories.map((category) => (
                        <li key={category.id}>
                            <CategoryTileHorizontal category={category} />
                        </li>
                    ))}
                </ul>

                {/* Mobile fallback - vertical layout */}
                <ul className="flex flex-wrap gap-x-5 gap-y-15 px-4 md:hidden">
                    {categories.map((category) => (
                        <li key={category.id} className="w-full">
                            <CategoryTileCard category={category} />
                        </li>
                    ))}
                </ul>

                {/* View All Products button section */}
                {categoryName && (
                    <div className="mt-7.5 flex flex-col items-center gap-2 pb-22.5 md:mt-[4.8rem]">
                        <Link
                            to={viewAllUrl}
                            className="flex h-10.5 min-w-42.5 items-center justify-center rounded-full border border-[#141414] bg-[#141414] px-6 text-4 font-medium text-[#f2f2f2] transition-colors duration-200 hover:bg-white hover:text-[#141414]"
                        >
                            View All {categoryName} Products
                        </Link>
                        {totalCount !== undefined && (
                            <span
                                className="text-4.5 text-[#8a8a8a]"
                                aria-live="polite"
                            >
                                {totalCount.toLocaleString()} Results
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Vertical variant - vertical cards
    return (
        <ul className="flex flex-wrap gap-x-5 gap-y-15 px-4 md:px-12">
            {categories.map((category) => (
                <li key={category.id} className="w-full md:w-86">
                    <CategoryTileCard category={category} />
                </li>
            ))}
        </ul>
    );
};

/**
 * Maximum number of subcategories to display in the category tile card.
 * Currently set to 5 so the cards align nicely in the grid and matches design requirements.
 * If a category has more than 5 subcategories, the "View all" link will be displayed.
 */
const MAX_SUBCATEGORIES = 5;

const CategoryTileCard = ({
    category,
}: {
    category: CategoryTileViewModel;
}) => {
    const visibleSubcategories =
        category.subCategories?.slice(0, MAX_SUBCATEGORIES) ?? [];

    return (
        <div className="flex flex-col gap-5">
            {/* Thumbnail with overlay text */}
            <Link
                to={category.link}
                aria-label={`View ${category.title} category${category.itemsCount !== undefined ? `, ${category.itemsCount.toLocaleString()} products` : ''}`}
                className="bg-neutral-200 group relative h-106.5 overflow-hidden rounded-lg"
            >
                {category.image.src && (
                    <Image
                        src={category.image.src}
                        alt=""
                        fill
                        fit="cover"
                        className="scale-[1.2] transition-transform duration-300 group-hover:scale-[1.25]"
                        multiplied={category.image.multiplied}
                        loading="lazy"
                    />
                )}
                {category.image.multiplied ? (
                    <span className="absolute inset-0 bg-[#D5D4D2]/20" />
                ) : (
                    <span className="absolute inset-0 bg-black/10" />
                )}
                <span className="absolute inset-0 bg-black/25" />
                <span className="absolute inset-0 flex items-center justify-center px-4">
                    <span className="text-center text-6 font-normal text-[#f2f2f2]">
                        {category.title}
                        {category.itemsCount !== undefined && (
                            <span className="ml-1.5 align-super text-xs">
                                {category.itemsCount.toLocaleString()}
                            </span>
                        )}
                    </span>
                </span>
            </Link>

            {/* Subcategory links */}
            <ul className="flex flex-col gap-px">
                {visibleSubcategories.map((sub, index) => (
                    <li
                        key={sub.id || index}
                        className="leading-[1rem] md:leading-[112.5%]"
                    >
                        <Link
                            to={sub.link}
                            className="text-4 font-normal leading-[1.125] tracking-[0.03rem] text-[#141414] hover:underline"
                        >
                            {sub.title}
                        </Link>
                    </li>
                ))}
                {/* Always show "View all" link */}
                <li className="mt-1.5 leading-[1rem] md:leading-[112.5%]">
                    <Link
                        to={category.link}
                        className="text-3.5 font-bold leading-[112.5%] tracking-[0.03rem] text-[#323232] hover:underline"
                    >
                        View all
                    </Link>
                </li>
            </ul>
        </div>
    );
};

/**
 * Horizontal tile variant for category display.
 * Image on left (~245px x 302px), text on right (~159px).
 * Used on first-level+ category pages (desktop only).
 */
const CategoryTileHorizontal = ({
    category,
}: {
    category: CategoryTileViewModel;
}) => {
    const visibleSubcategories =
        category.subCategories?.slice(0, MAX_SUBCATEGORIES) ?? [];

    return (
        <div className="flex gap-5">
            {/* Thumbnail with overlay text - left side */}
            <Link
                to={category.link}
                aria-label={`View ${category.title} category${category.itemsCount !== undefined ? `, ${category.itemsCount.toLocaleString()} products` : ''}`}
                className="bg-neutral-200 group relative h-75.5 w-61 shrink-0 overflow-hidden rounded-lg"
            >
                {category.image.src && (
                    <Image
                        src={category.image.src}
                        alt=""
                        fill
                        fit="cover"
                        className="scale-[1.2] transition-transform duration-300 group-hover:scale-[1.25]"
                        multiplied={category.image.multiplied}
                        loading="lazy"
                    />
                )}
                {category.image.multiplied ? (
                    <span className="absolute inset-0 bg-[#D5D4D2]/20" />
                ) : (
                    <span className="absolute inset-0 bg-black/10" />
                )}
                <span className="absolute inset-0 bg-black/25" />
                <span className="absolute inset-0 flex items-center justify-center px-4">
                    <span className="text-center text-6 font-normal leading-[85.6%] text-[#f2f2f2]">
                        {category.title}
                        {category.itemsCount !== undefined && (
                            <span className="ml-1 align-super text-3">
                                {category.itemsCount.toLocaleString()}
                            </span>
                        )}
                    </span>
                </span>
            </Link>

            {/* Text content - right side */}
            <div className="gap-2.25 flex w-40 flex-col">
                {/* Category title */}
                <Link
                    to={category.link}
                    className="text-4 font-medium leading-[112.5%] tracking-[-0.03em] text-[#141414] hover:underline"
                >
                    {category.title}
                </Link>

                {/* Subcategory links */}
                {visibleSubcategories.map((sub, index) => (
                    <Link
                        key={sub.id || index}
                        to={sub.link}
                        className="mt-[0.5625rem] text-4 font-normal leading-[112.5%] tracking-[-0.03em] text-[#141414] hover:underline"
                    >
                        {sub.title}
                    </Link>
                ))}

                {/* View all link */}
                <Link
                    to={category.link}
                    className="mt-[0.5625rem] text-4 font-medium leading-[112.5%] tracking-[-0.03em] text-[#141414] hover:underline"
                >
                    View all
                </Link>
            </div>
        </div>
    );
};
